'use client';

import type { AppointmentType } from '@provider-directory/contexts/AppointmentContext';
import type { LocationResource } from '@provider-directory/types/locations.contracts';
import type {
  ProviderResource,
  ProvidersSearchCriteria,
} from '@provider-directory/types/providers.contracts';
import {
  AppPageComponent,
  AppPageName,
  SchedulingFlowType,
} from './conversions';

export type { ConversionType } from './conversions';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    mixpanel?: {
      /* eslint-disable @typescript-eslint/naming-convention */
      start_session_recording: () => void;
      stop_session_recording: () => void;
      register_once: (props: Record<string, string>) => void;
      /* eslint-enable @typescript-eslint/naming-convention */
    };
  }
}

export const CROSS_DOMAIN_PARAMS = [
  'fabric_sid',
  'customer_id',
  'referrer',
] as const;

/**
 * Events that should only be sent to Mixpanel and NOT to GTM/dataLayer.
 * Use this for internal analytics events that shouldn't be shared with customers.
 */
export const INTERNAL_ONLY_EVENTS = ['CACHED_LIVE_SLOTS_DISCREPANCY'] as const;

/**
 * GA event taxonomy (originally from Montefiore GA spec)
 *
 * https://docs.google.com/presentation/d/1lHjjE_T9uhMzLYzbo-E5kVVyyisBjP5nqSIvo0qVqh0
 */
type GAEventType =
  | 'componentClick'
  | 'navigationClick'
  | 'mediaEngagement'
  | 'searchClick'
  | 'search'
  | 'dropdownSearch'
  | 'conversionClick'
  | 'articleClick';
export type GAEventName =
  | `e_${GAEventType}`
  | 'CONVERSION'
  | 'CLICKED_BOOK_ANOTHER_PROVIDER';
export type GANavigationType =
  | 'top-bar'
  | 'footer'
  | 'breadcrumb'
  | 'inline-menu'
  | 'inline-navigation'
  | 'share'
  | 'side-bar'
  | 'tabs';
export type GAComponentType =
  | 'dropdown'
  | 'media image'
  | 'media video'
  | 'modal'
  | 'visual list';
export type BooleanString = 'true' | 'false';
export type LinkType = 'link' | 'button';

export interface BaseAnalyticsEvent {
  event: GAEventName;
}

export interface SchedulingPageViewEvent {
  url?: string;
  path?: string;
  doctorId: string;
  gender: string;
  assocDepartment: string;
  nameSelect: string;
  location: string;
  locationId: string;
  reasonForVisit: string;
  visitTypeId: string;
}

export interface ComponentClickEvent extends BaseAnalyticsEvent {
  event: 'e_componentClick';
  componentType: GAComponentType;
  clickText: string;
  linkType?: string;
  itemTitle?: string;
  phoneNumber?: string;
  clickSlot?: string;
}

export interface NavigationClickEvent extends BaseAnalyticsEvent {
  event: 'e_navigationClick';
  navigationType: GANavigationType;
  linkType: LinkType;
  clickText: string;
}

export interface SearchClickEvent extends BaseAnalyticsEvent {
  event: 'e_searchClick';
  componentType: GAComponentType;
  clickText: string;
  linkType?: string;
  filtersEmployed?: string;
  searchString?: string;
}

export interface SearchEvent extends BaseAnalyticsEvent {
  event: 'e_search';
  appType?: string;
  specialty?: string;
  zip?: string;
  location?: string;
  primaryCareChecked?: BooleanString;
  pediatricCareChecked?: BooleanString;
}

export interface DropdownSearchEvent extends BaseAnalyticsEvent {
  event: 'e_dropdownSearch';
  appType: string;
  nameSelect: string;
}

export interface ClickedBookAnotherProviderEvent extends BaseAnalyticsEvent {
  event: 'CLICKED_BOOK_ANOTHER_PROVIDER';
  appType: string;
  pNPI: string;
  pName: string;
  pGender?: string;
  pLanguage?: string;
  pSpecialty?: string;
  pLocation?: string;
  pSchedulingType?: string;
  pHospitalGroup?: string;
  pEmploymentStatus?: string;
  schedulingFlowType?: SchedulingFlowType;
  linkContextPage: AppPageName;
  linkContextComponent?: AppPageComponent;
  fSpecialty?: string;
  fLocation?: string;
  fDistance?: string;
  fPCP?: string;
  fOnlineScheduling?: string;
  fHospitalGroup?: string;
  fGender?: string;
  fLanguage?: string;
  fAgeGroup?: string;
}

export interface ConversionProviderFields {
  pNPI?: string;
  pName?: string;
  pSpecialty?: string;
  pGender?: string;
  pLanguage?: string;
  pLocation?: string;
  pPhone?: string;
  pSchedulingType?: string;
  pHospitalGroup?: string;
  pEmploymentStatus?: string;
}
export interface ConversionSearchFilterFields {
  fSpecialty?: string;
  fOnlineScheduling?: string;
  fPCP?: string;
  fHospitalGroup?: string;
  fGender?: string;
  fLanguage?: string;
  fAgeGroup?: string;
  fLocation?: string;
  fDistance?: string;
}
export interface DTreeEventBase {
  questionId: string;
  specialtyBranch?: string;
  referringUrl: string;
  pNPI: string;
  pGender?: string;
  pSpecialty: string;
  pName: string;
  pLanguage?: string;
  pLocation?: string;
  pLocationId?: string;
  pHospitalGroup?: string;
  pEmploymentStatus?: string;
  schedulingFlowType?: SchedulingFlowType;
  appType: string;
}

export interface DTreeQuestionShownEvent extends DTreeEventBase {}

export interface DTreeAnswerSubmittedEvent extends DTreeEventBase {
  answer: string;
  locationId?: string;
  reasonForVisit?: string;
  visitTypeId?: string;
}

export interface CachedLiveSlotsDiscrepancyEvent {
  appType: string;
  pNPI: string;
  pName: string;
  pSpecialty?: string;
  pGender?: string;
  pLanguage?: string;
  pLocation?: string;
  pLocationId: string;
  selectedDate: string;
  cachedSlotsCount: number;
  liveSlotsCount: number;
  reasonForVisit: string;
  linkContextPage: AppPageName;
  linkContextComponent?: AppPageComponent;
}

export type EventPayload = Record<string, string | undefined>;

export interface StandardEventPayloadOptions {
  provider?: ProviderResource;
  phoneNumber?: string;
  searchCriteria?: Partial<ProvidersSearchCriteria>;
  linkContextPage: AppPageName;
  linkContextComponent?: AppPageComponent;
  clickText?: string;
  clickURL?: string;
  filterSpecialtyDisplayName?: string;
  schedulingFlowType?: SchedulingFlowType;
}

export interface EhrRedirectEventOptions extends StandardEventPayloadOptions {
  location?: LocationResource;
  visitTypeId?: string;
  redirectUrl?: string;
}

export interface ClickedProviderLinkEventOptions
  extends StandardEventPayloadOptions {
  mediaType?: string;
  source?: string;
  date?: string;
}

export type FormStep = 'PatientInfo' | 'InsuranceInfo' | 'AppointmentReview';

export interface CompletedFormEventOptions extends StandardEventPayloadOptions {
  formStep: FormStep;
}

export interface ChangedSortEventOptions extends StandardEventPayloadOptions {
  sortValue: string;
  previousSortValue?: string;
}

export type AnalyticsEvent =
  | SchedulingPageViewEvent
  | ComponentClickEvent
  | NavigationClickEvent
  | SearchClickEvent
  | SearchEvent
  | DropdownSearchEvent
  | ClickedBookAnotherProviderEvent;

export type TrackingFn<T extends AnalyticsEvent> = (
  payload: Omit<T, 'event'>,
) => void;

export interface SchedulingEventOptions {
  provider?: ProviderResource | null;
  appointment?: AppointmentType;
  schedulingFlowType?: SchedulingFlowType;
  appointmentId?: string;
  errorCode?: string | null;
  linkContextPage: AppPageName;
  linkContextComponent?: AppPageComponent;
}

export interface BookAnotherProviderEventOptions {
  provider: ProviderResource;
  searchCriteria?: Partial<ProvidersSearchCriteria>;
  linkContextPage: AppPageName;
  linkContextComponent?: AppPageComponent;
}

export interface CachedLiveSlotsDiscrepancyEventOptions {
  provider: ProviderResource;
  location?: LocationResource;
  selectedDate: string;
  cachedSlotsCount: number;
  liveSlotsCount: number;
  reasonForVisit: string;
  linkContextPage: AppPageName;
  linkContextComponent?: AppPageComponent;
}

export interface UpdatedSearchFilterEventOptions {
  filter: string;
  value: unknown;
  searchParams: Record<string, unknown>;
  linkContextPage: AppPageName;
  linkContextComponent?: AppPageComponent;
}
